import { useEffect, useMemo, useRef, useState } from 'react'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { useAuth } from '../../context/useAuth'
import { useChat } from '../../context/useChat'
import { useChatSelection } from '../../context/chatSelectionContextCore'
import { useWorkspace } from '../../context/useWorkspace'
import {
  subscribeAiChatSessions,
  type AiChatAttachment,
  type AiChatSession,
} from '../../services/aiChatHistoryService'
import type { AiOperatingMode } from '../../types/ai'
import {
  buildChannelToken,
  chatSelectionTokenKey,
  chatSourceContextToAttachment,
  materializeChatSelectionTokens,
  type AiChatHandoffTarget,
} from '../../types/aiChatSource'
import { Button } from '../Common/Button'
import { IconButton } from '../Common/IconButton'
import { Pressable } from '../Common/Pressable'
import { FloatingMenu } from '../Common/FloatingMenu'
import { OverflowTip } from '../Common/Hover'
import { RailChipTabs } from '../Common'

export const ChatAiMagicPanel = ({
  onOpenAiMagic,
  onOpenAiMagicSession,
  onSendChatsToAi,
  hideWhenEmpty = false,
}: {
  onOpenAiMagic?: () => void
  onOpenAiMagicSession?: (session: AiChatSession) => void
  onSendChatsToAi?: (payload: { attachments: AiChatAttachment[]; target: AiChatHandoffTarget }) => void
  hideWhenEmpty?: boolean
}) => {
  const { currentUser } = useAuth()
  const { activeWorkspaceId } = useWorkspace()
  const { activeChannelId, channelsList, messagesStream } = useChat()
  const selection = useChatSelection()
  const [sessions, setSessions] = useState<AiChatSession[]>([])
  const [scope, setScope] = useState<'all' | 'channel'>('all')
  const [menuOpen, setMenuOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!activeWorkspaceId || !currentUser) return
    return subscribeAiChatSessions(activeWorkspaceId, currentUser.uid, setSessions)
  }, [activeWorkspaceId, currentUser])

  const activeChannel = useMemo(
    () => channelsList.find((channel) => channel.id === activeChannelId) ?? null,
    [activeChannelId, channelsList],
  )
  const messagesByChannel = useMemo(
    () => activeChannelId ? { [activeChannelId]: messagesStream } : {},
    [activeChannelId, messagesStream],
  )
  const materializedAttachments = useMemo(
    () => materializeChatSelectionTokens(selection.tokens, {
      channels: channelsList,
      messagesByChannel,
    }).map(chatSourceContextToAttachment),
    [channelsList, messagesByChannel, selection.tokens],
  )
  const selectedCharCount = materializedAttachments.reduce((sum, attachment) => sum + (attachment.text?.length ?? 0), 0)
  // Both tabs are chat-sourced only — this card is a Chat add-on to AI Magic,
  // not a mirror of the workspace-wide AI history. "All channels" = every
  // chat-sourced session; "This channel" narrows to sessions carrying a chat
  // attachment from the currently open channel.
  const visibleSessions = useMemo(() => {
    const chatSourced = sessions.filter((session) => (session.attachments ?? []).some((attachment) => attachment.kind === 'chat'))
    if (scope === 'all' || !activeChannelId) return chatSourced
    return chatSourced.filter((session) => (session.attachments ?? []).some((attachment) => attachment.kind === 'chat' && attachment.channelId === activeChannelId))
  }, [activeChannelId, scope, sessions])

  const send = (target: AiChatHandoffTarget) => {
    if (materializedAttachments.length === 0) return
    onSendChatsToAi?.({ attachments: materializedAttachments, target })
    selection.setMode('list')
    selection.clear()
    setMenuOpen(false)
  }

  const addActiveChannel = () => {
    if (!activeChannel) return
    selection.toggleToken(buildChannelToken(activeChannel))
  }

  if (hideWhenEmpty && visibleSessions.length === 0 && selection.tokens.length === 0 && selection.mode !== 'select') {
    return null
  }

  return (
    <section className="railPageShell__card--sidecar chatPage3C__sidecarCard chatPage3C__sidecarCard--ai" aria-label="AI Magic">
      <div className="chatPage3C__sidecarCardHead">
        <h3 className="chatPage3C__sidecarCardTitle">AI Magic</h3>
        <Pressable
          className={`chatAiMagicModePill${selection.mode === 'select' ? ' isActive' : ''}`}
          onClick={() => selection.setMode(selection.mode === 'select' ? 'list' : 'select')}
          aria-pressed={selection.mode === 'select'}
        >
          <AutoAwesomeRoundedIcon fontSize="inherit" />
          <span>{selection.mode === 'select' ? 'Selecting' : 'Select'}</span>
        </Pressable>
      </div>

      {selection.mode === 'select' ? (
        <div className="chatAiMagicSelect">
          <p className="chatAiMagicHint">Click any message, channel, or tag to add it to your AI selection.</p>
          {activeChannel ? (
            <Button
              size="sm"
              variant="tertiary"
              className="aiChatNewButton chatAiMagicAddChannel"
              onClick={addActiveChannel}
              icon={<AddRoundedIcon fontSize="inherit" />}
            >
              <span>Attach #{activeChannel.slug || activeChannel.name}</span>
            </Button>
          ) : null}
          <div className="chatAiMagicTokenList">
            {selection.tokens.map((token) => {
              const key = chatSelectionTokenKey(token)
              const label = token.kind === 'message'
                ? `${token.authorName}: ${token.preview || 'Message'}`
                : token.kind === 'channel'
                  ? `#${token.channelName}`
                  : `#${token.tag}`
              const meta = token.kind === 'message'
                ? `#${token.channelName}`
                : token.kind === 'tag'
                  ? token.scope
                  : 'Last messages'
              return (
                <div key={key} className="chatAiMagicToken">
                  <ChatBubbleOutlineRoundedIcon fontSize="small" />
                  <span><strong>{label}</strong><small>{meta}</small></span>
                  <IconButton
                    size="sm"
                    variant="tertiary"
                    onClick={() => selection.removeToken(key)}
                    label={`Remove ${label}`}
                    icon={<CloseRoundedIcon fontSize="inherit" />}
                  />
                </div>
              )
            })}
            {selection.tokens.length === 0 ? <p className="chatPage3C__empty">No chat sources selected.</p> : null}
          </div>
          <div className="chatAiMagicSelectFooter">
            <span>{materializedAttachments.length} bundle{materializedAttachments.length === 1 ? '' : 's'} · {selectedCharCount.toLocaleString()} chars</span>
            <Button
              ref={triggerRef}
              size="sm"
              variant="primary"
              disabled={materializedAttachments.length === 0}
              onClick={() => setMenuOpen((value) => !value)}
              aria-expanded={menuOpen}
              trailingIcon={<ExpandMoreRoundedIcon fontSize="inherit" />}
            >
              <span>Send to AI</span>
            </Button>
            <FloatingMenu anchorRef={triggerRef} isOpen={menuOpen} onClose={() => setMenuOpen(false)} placement="top-end" className="aiChatHistoryMenu" role="menu" ariaLabel="Send chats to AI Magic">
              <Pressable role="menuitem" onClick={() => send({ kind: 'new', mode: 'CREATE' as AiOperatingMode })}>New session · Generate</Pressable>
              <Pressable role="menuitem" onClick={() => send({ kind: 'new', mode: 'EXECUTE' as AiOperatingMode })}>New session · Automate</Pressable>
              {sessions.slice(0, 6).map((session) => (
                <Pressable key={session.id} role="menuitem" onClick={() => send({ kind: 'existing', sessionId: session.id })}>
                  Append · {session.title || 'New chat'}
                </Pressable>
              ))}
            </FloatingMenu>
          </div>
        </div>
      ) : (
        <div className="chatAiMagicList">
          <RailChipTabs<'all' | 'channel'>
            items={[
              { key: 'all', label: 'All channels' },
              { key: 'channel', label: 'This channel' },
            ]}
            value={scope}
            onChange={setScope}
            ariaLabel="AI Magic chat source filter"
            orientation="horizontal"
            className="chatAiMagicTabs"
          />
          <div className="aiChatHistoryList chatAiMagicRows">
            {visibleSessions.map((session) => {
              const chatAttachments = (session.attachments ?? []).filter((attachment) => attachment.kind === 'chat')
              const first = chatAttachments[0]
              const sourceLabel = first ? `${first.messages?.length ?? 0} chats · #${first.channelName ?? first.channelId ?? 'chat'}` : null
              return (
                <div key={session.id} className="aiChatHistoryRow">
                  <Pressable
                    className="aiChatHistoryOpen"
                    onClick={() => {
                      if (onOpenAiMagicSession) onOpenAiMagicSession(session)
                      else onOpenAiMagic?.()
                    }}
                  >
                    <OverflowTip label={session.title || 'New chat'} placement="right-start" group="chatAiMagicPanel" offset={14}>
                      <span className="aiChatHistoryOpenLabel">{session.title || 'New chat'}</span>
                    </OverflowTip>
                    {sourceLabel ? (
                      <span className="chatAiMagicSourcePill">
                        <ChatBubbleOutlineRoundedIcon fontSize="inherit" />
                        {sourceLabel}
                      </span>
                    ) : null}
                  </Pressable>
                </div>
              )
            })}
            {visibleSessions.length === 0 ? (
              <p className="chatPage3C__empty">
                {scope === 'channel'
                  ? 'No AI Magic sessions started from this channel yet.'
                  : 'No AI Magic sessions started from chats yet.'}
              </p>
            ) : null}
          </div>
        </div>
      )}
    </section>
  )
}
